/**
 * @copyright 2022 Nuance Communications Inc.
 * All Rights Reserved.
 */
import * as React from "react";

import FieldDefinitions from "./FieldDefinitions";
import PreviewComponent from "./PreviewComponent";
import ViewActions from "./ViewActions";
import ViewHeader from "./ViewHeader";
import ViewTitle from "./ViewTitle";
import { Separator, Stack } from "@fluentui/react";
import "../../node_modules/office-ui-fabric-core/dist/css/fabric.min.css";
import { IMainFrameViewModel } from "../Mainframe/MainframeViewModel";
import { IMainViewState, MainViewCommands } from "./MainViewViewModel";
import styles from "./MainView.module.css";
import { ControlType } from "@nuance/hierarchical-settings-ui-domain";

export interface IMainViewProps {
    mainFrameVM: IMainFrameViewModel;
    frozenControlGuids?: Set<string>;
}

const mainViewCommands = new MainViewCommands();
const cmds = mainViewCommands.Cmds;

const MainView = (props: IMainViewProps): JSX.Element => {
    if (props.mainFrameVM.state.definition === undefined) {
        throw new Error("Definition should not be undefined here.");
    }

    const initialState: IMainViewState = {
        definition: props.mainFrameVM.state.definition,
        mainFrameVM: props.mainFrameVM
    };

    const frozenControlGuids: Set<string> = new Set<string>(
        initialState.definition?.UI?.Controls?.map(c => c.CtrlGuid)
    );

    initialState.definition?.UI?.Controls?.forEach(c => {
        if (
            c.ControlDiscriminator === ControlType.DropdownField ||
            c.ControlDiscriminator === ControlType.RadioField
        ) {
            c.Items?.forEach(i => frozenControlGuids.add(i.ItemGuid));
        }
    });

    const [state, dispatch] = React.useReducer(mainViewCommands.Reducer, initialState);
    const mainViewVM = { state, cmds, dispatch };

    return (
        <>
            <ViewHeader />
            {state.definition && (
                <div className={"ms-Fabric " + styles.mainView} dir="ltr">
                    <div className="ms-Grid">
                        <div className="ms-Grid-row">
                            <div className="ms-Grid-col ms-sm4">
                                <ViewTitle
                                    settingGroupName={state.definition.SettingGroupName}
                                    evaluation={state.definition.EvaluationStrategy}
                                />
                            </div>
                            <div className="ms-Grid-col ms-sm8">
                                <Stack horizontalAlign="end">
                                    <ViewActions definition={state.definition} />
                                </Stack>
                            </div>
                        </div>

                        <Separator />

                        <div className="ms-Grid-row">
                            <div className="ms-Grid-col ms-sm4 ">
                                <FieldDefinitions
                                    mainViewVM={mainViewVM}
                                    settingDefinition={state.definition}
                                    frozenControlGuids={frozenControlGuids}
                                />
                            </div>
                            <div className="ms-Grid-col ms-sm8 ">
                                <PreviewComponent
                                    settingDefinition={state.definition}
                                    mainViewVM={mainViewVM}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default MainView;
